import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "techquote_column_widths_v1";

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
}

function writeStored(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* best-effort */
  }
}

/**
 * Manage column widths for a dynamic grid.
 *
 *  - `defaults`: { [key]: pixelWidth } expected order
 *  - Returns `widths` state, a `startResize(key, event)` handler, a
 *    `autoFit(key, measuredPx)` helper and `reset()` to clear storage.
 *
 * Persists to localStorage key `techquote_column_widths_v1` on mouseup.
 */
export default function useColumnResize(defaults, { min = 40, max = 400 } = {}) {
  const [widths, setWidths] = useState(() => {
    const stored = readStored();
    return { ...defaults, ...stored };
  });

  // If the set of keys changes (e.g. different family's columns), fill in
  // missing entries with their default without losing user overrides.
  const defaultsKeys = Object.keys(defaults).join("|");
  useEffect(() => {
    setWidths((prev) => ({ ...defaults, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsKeys]);

  const dragRef = useRef(null);

  const startResize = useCallback(
    (key, e) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = widths[key] ?? defaults[key] ?? 120;
      dragRef.current = { key, startX, startW };
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      const onMove = (ev) => {
        const st = dragRef.current;
        if (!st) return;
        const dx = ev.clientX - st.startX;
        const w = Math.max(min, Math.min(max, st.startW + dx));
        setWidths((prev) => ({ ...prev, [st.key]: w }));
      };
      const onUp = () => {
        const st = dragRef.current;
        dragRef.current = null;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        if (st) {
          setWidths((prev) => {
            writeStored(prev);
            return prev;
          });
        }
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [widths, defaults, min, max]
  );

  const autoFit = useCallback(
    (key, measuredPx) => {
      const w = Math.max(min, Math.min(max, Math.round(measuredPx + 20)));
      setWidths((prev) => {
        const next = { ...prev, [key]: w };
        writeStored(next);
        return next;
      });
    },
    [min, max]
  );

  // Reset a single column to its default width (double-click on handle).
  const resetOne = useCallback(
    (key) => {
      const def = defaults[key];
      if (def == null) return;
      setWidths((prev) => {
        const next = { ...prev, [key]: def };
        writeStored(next);
        return next;
      });
    },
    [defaults]
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setWidths({ ...defaults });
  }, [defaults]);

  return { widths, startResize, autoFit, resetOne, reset };
}
